import React, { useEffect, useRef, useState } from "react";
import {
  Image,
  View,
  Text,
  StyleSheet,
  useWindowDimensions,
  Pressable,
  Linking,
  Animated,
  TextStyle,
  Platform,
} from "react-native";
import * as IMAGES from "../../resources/IMAGES";
import { theme } from "../../theme/theme";
import {
  getStoreUrl,
  isAnimationLoaded,
  setAnimationLoaded,
} from "@lib/utilities/HelperFunctions";
import { QR_WIDTH_CONSTANTS } from "@lib/utilities/Constants";
import DeviceInfo from "react-native-device-info";
import { useAppDispatch, useAppSelector } from "@lib";

const SmartLinksComponent = () => {
  const { width } = useWindowDimensions();
  if (Platform.OS == "web") {
    const navigation: any = performance.getEntriesByType("navigation")[0];
    if (navigation.type === "reload") {
      setAnimationLoaded(false);
    }
  }

  // Dynamic scaling: Adjusts font/spacing based on screen width
  const desktopDevices = width < QR_WIDTH_CONSTANTS.desktopDevice;
  const isTablets = width < QR_WIDTH_CONSTANTS.tablets;
  const isMobile = width < QR_WIDTH_CONSTANTS.isMobile;
  const isStage = process.env.REACT_APP_ENV === "stage";
  const translateY = useRef(new Animated.Value(200)).current; // Start off-screen (bottom)
  const opacity = useRef(new Animated.Value(0)).current; // Start invisible
  const [storeIcon, setStoreIcon] = useState(IMAGES.APP_STORE_ICON);

  const dispatch = useAppDispatch();

  useEffect(() => {
    const getIcon = async () => {
      const deviceOS = (await DeviceInfo.getBaseOs()).toLocaleLowerCase();
      if (
        deviceOS === "ios" ||
        deviceOS === "iphone" ||
        deviceOS === "mac os"
      ) {
        setStoreIcon(IMAGES.APP_STORE_ICON);
      } else {
        setStoreIcon(IMAGES.PLAY_STORE_ICON);
      }
    };

    const loadAnimation = async () => {
      if (Platform.OS == "web") {
        if (isAnimationLoaded()) {
          translateY.setValue(0);
          opacity.setValue(1);
          return;
        }

        setTimeout(() => {
          if (process.env.REACT_APP_IS_QR_VISIBLE) {
            Animated.parallel([
              Animated.spring(translateY, {
                toValue: 0,
                useNativeDriver: true,
                speed: 1,
                bounciness: 1,
              }),
              Animated.timing(opacity, {
                toValue: 1,
                duration: 600,
                useNativeDriver: true,
              }),
            ]).start(() => {
              setAnimationLoaded(true);
            });
          }
        }, 800);
      }
    };

    getIcon();
    loadAnimation();
  }, []);

  const handleStorePress = async (store?: "ios" | "android") => {
    const url = await getStoreUrl(store);
    if (url) {
      Linking.openURL(url).catch((err) => {});
    }
  };

  const animatedComponent = () => {
    return (
      <>
        <Image
          source={IMAGES.SHAREABLE_LINKS_ICON}
          style={[
            styles.mainIcon,
            {
              height: 143,
              width: 128,
              marginTop: isTablets ? -60 : -30,
            },
          ]}
          resizeMode="contain"
        />
        <View
          style={[
            styles.contentWrapper,
            { flexDirection: isTablets ? "column" : "row" },
          ]}
        >
          <Text
            style={[
              styles.text,
              {
                fontSize: desktopDevices
                  ? theme.fontsize.cs
                  : theme.fontsize.mmd,
                textAlign: isTablets ? "center" : "left",
              },
            ]}
          >
            Try our New App! {!isTablets ? "Scan" : "Click"} to download{" "}
            {!isTablets && "on Android & iOS"}
          </Text>

          {isTablets ? (
            <Pressable onPress={() => handleStorePress()}>
              <Image
                resizeMode="contain"
                source={storeIcon}
                style={styles.storeIcon}
              />
            </Pressable>
          ) : (
            <View style={styles.storeContainer}>
              <Pressable onPress={() => handleStorePress("android")}>
                <Image
                  resizeMode="contain"
                  source={IMAGES.PLAY_STORE_ICON}
                  style={styles.storeIcon}
                />
              </Pressable>
              <Pressable onPress={() => handleStorePress("ios")}>
                <Image
                  resizeMode="contain"
                  source={IMAGES.APP_STORE_ICON}
                  style={styles.storeIcon}
                />
              </Pressable>
            </View>
          )}
        </View>
        {!isTablets && (
          <View
            style={{
              flexDirection: "row",
              alignItems: "center",
              flex: 1,
            }}
          >
            <Image
              resizeMode="contain"
              source={IMAGES.OR_SEPERATOR_ICON}
              style={styles.separator}
            />

            <View>
              <Image
                resizeMode="contain"
                source={
                  isStage ? IMAGES.SHAREABLE_STAGE_ICON : IMAGES.SHAREABLE_ICON
                }
                style={styles.qrIcon}
              />
            </View>
          </View>
        )}
      </>
    );
  };

  return (
    <>
      {!isAnimationLoaded() ? (
        <Animated.View
          style={[
            styles.container,
            { flexDirection: isTablets ? "column" : "row" },
            {
              opacity,
              transform: [{ scale: isMobile ? 0.9 : 1 }, { translateY }],
            },
          ]}
        >
          {animatedComponent()}
        </Animated.View>
      ) : (
        <View
          style={[
            styles.container,
            { flexDirection: isTablets ? "column" : "row" },
          ]}
        >
          {animatedComponent()}
        </View>
      )}
    </>
  );
};

const styles = StyleSheet.create({
  buttonLogin: {
    paddingHorizontal: theme.spacing.xxl5,
    paddingVertical: 10,
    backgroundColor: theme.colors.black,
    marginVertical: 15,
    textAlign: "center",
    display: "flex",
    alignItems: "center",
    borderRadius: theme.radius.xxs,
    cursor: "pointer",
    marginLeft: "auto",
    marginRight: "auto",
  },
  whiteText: {
    textAlign: "center",
    color: theme.colors.text.white,
    // fontFamily: theme.fontFamily.robotoRegular,
    fontSize: theme.fontsize.reg,
    fontFamily: theme.fontFamily.robotoSlabBold,
    fontWeight: theme.fontweight.bold as TextStyle["fontWeight"],
  },

  container: {
    alignSelf: "center",
    backgroundColor: `${theme.colors.black}40`,
    borderRadius: 20,
    paddingHorizontal: 15,
    alignItems: "center",
    justifyContent: "space-between",
    width: "90%",
    // @ts-ignore
    backdropFilter: "blur(10px)",
  },
  mainIcon: {},
  contentWrapper: {
    flex: 4,
    paddingHorizontal: 10,
    justifyContent: "space-between",
    alignItems: "center",
  },
  text: {
    fontSize: theme.fontsize.mmd,
    textAlign: "left",
    flex: 3,
    paddingHorizontal: 10,
    justifyContent: "center",
    fontFamily: theme.fontFamily.robotoSlabBold,
    fontWeight: theme.fontweight.bold as TextStyle["fontWeight"],
    fontStyle: "normal",
    letterSpacing: 0,

    color: theme.colors.white,
  },
  storeContainer: {
    flexDirection: "row",
    gap: 10,
    alignItems: "center",
  },
  storeIcon: {
    height: 60,
    width: 120,
  },
  separator: {
    flex: 0.8,
    height: 80,
  },
  qrIcon: {
    height: 82,
    width: 82,
    borderRadius: 10,
    marginLeft: 10,
    alignSelf: "center",
  },
});

export default SmartLinksComponent;
